import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuCalendarCheck, LuCalendarDays, LuChevronRight, LuFolderKanban, LuLoaderCircle, LuTag, LuUserCog } from 'react-icons/lu';
import DetailPane from '../../../components/data/DetailPane';
import useEmployeeReportText from '../employeeReportText';
import { GRADIENT, initialsOf } from './AssignmentCell';

const SECTION_TITLE = 'tw-px-2 tw-pb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

/**
 * The selected assignment: its visits (ProjectVisitsModal, after checking
 * there are any — as before), its details and the worker's other project
 * assignments (pick one to switch).
 */
const AssignmentDetailPane = ({ docked, height, row, otherRows, isAdmin, checkingVisits, position, onPrev, onNext, onClose, onVisits, onSelectRow }) => {
  const { label, formatDate } = useEmployeeReportText();
  const paneLabel = label('panel.label');

  if (!row) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const others = otherRows.filter((item) => item.id !== row.id);

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={row.id}
      identity={{
        avatar: initialsOf(row),
        gradient: GRADIENT,
        title: row.fullName || '-',
        subtitle: [row.projectTitle, row.role].filter(Boolean).join(' · ') || undefined,
      }}
      actions={[
        { key: 'visits', label: label('panel.visits'), icon: checkingVisits ? LuLoaderCircle : LuCalendarCheck, spin: checkingVisits, tint: 'blue', onClick: onVisits, disabled: checkingVisits },
      ]}
      details={[
        { key: 'project', icon: LuFolderKanban, label: label('columns.project'), value: row.projectTitle || '-' },
        { key: 'center', icon: LuBuilding2, label: label('columns.center'), value: row.centerName || '-' },
        { key: 'role', icon: LuUserCog, label: label('columns.role'), value: row.role || '-' },
        ...(isAdmin ? [{ key: 'rate', icon: LuTag, label: label('columns.rate'), value: row.rateText || '-' }] : []),
        { key: 'assigned', icon: LuCalendarDays, label: label('columns.assignedAt'), value: formatDate(row.assignedAt) || '-' },
      ]}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    >
      {others.length > 0 && (
        <section aria-label={label('panel.otherAssignments')} className="tw-px-1">
          <p className={SECTION_TITLE}>{label('panel.otherAssignments')} · {others.length}</p>
          <ul className="tw-space-y-1">
            {others.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onSelectRow(item)}
                  className="tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-xl tw-px-2.5 tw-py-2 tw-text-left hover:tw-bg-slate-900/[0.04] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/40 dark:hover:tw-bg-white/[0.05]"
                >
                  <span className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300">
                    <LuFolderKanban aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </span>
                  <span className="tw-min-w-0 tw-flex-1">
                    <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{item.projectTitle || '-'}</span>
                    <span className="tw-block tw-truncate tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">{[item.role, item.centerName].filter(Boolean).join(' · ') || '-'}</span>
                  </span>
                  <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </DetailPane>
  );
};

AssignmentDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  row: PropTypes.object,
  otherRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  isAdmin: PropTypes.bool,
  checkingVisits: PropTypes.bool,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onVisits: PropTypes.func.isRequired,
  onSelectRow: PropTypes.func.isRequired,
};

export default AssignmentDetailPane;
